import type {
  CreateFormRequest,
  FormDetailResponse,
  FormFieldDefinition,
  FormStatus,
  FormSubmissionResponse,
  FormSummaryResponse,
  PaginatedResponse,
  UpdateFormRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListFormsParams {
  readonly search?: string | undefined;
  readonly status?: FormStatus | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly limit?: number | undefined;
}

export const formsApi = {
  list(params: ListFormsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<FormSummaryResponse>>('/forms', {
      query: { ...params },
      signal,
    });
  },

  get(id: string, signal?: AbortSignal) {
    return apiRequest<FormDetailResponse>(`/forms/${id}`, { signal });
  },

  create(body: CreateFormRequest) {
    return apiRequest<FormDetailResponse>('/forms', { method: 'POST', body });
  },

  update(id: string, body: UpdateFormRequest) {
    return apiRequest<FormDetailResponse>(`/forms/${id}`, { method: 'PATCH', body });
  },

  updateFields(id: string, fields: readonly FormFieldDefinition[]) {
    return apiRequest<FormDetailResponse>(`/forms/${id}`, { method: 'PATCH', body: { fields } });
  },

  publish(id: string) {
    return apiRequest<FormDetailResponse>(`/forms/${id}/publish`, { method: 'POST' });
  },

  /** There is no delete. Submissions reference a form long after it stops collecting. */
  archive(id: string, reason: string) {
    return apiRequest<FormDetailResponse>(`/forms/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restore(id: string) {
    return apiRequest<FormDetailResponse>(`/forms/${id}/restore`, { method: 'POST' });
  },

  // --- Submissions ------------------------------------------------------------

  listSubmissions(id: string, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<FormSubmissionResponse>>(`/forms/${id}/submissions`, {
      signal,
    });
  },

  submit(id: string, body: { personId?: string | null; answers: Record<string, unknown> }) {
    return apiRequest<FormSubmissionResponse>(`/forms/${id}/submissions`, {
      method: 'POST',
      body,
    });
  },
};
